/**
 * Toast store — framework-free, usable outside React.
 *
 * Owns ids, auto-dismiss timers and the live-toast cap. Renderers (web, native)
 * subscribe and draw; they never own timers.
 */

import type {
  ToastApi,
  ToastItem,
  ToastListener,
  ToastOptions,
  ToastStore,
  ToastStoreOptions,
  ToastVariant,
} from "./types";

export const TOAST_DEFAULT_MAX = 5;

export const TOAST_DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 4000,
  error: 6000,
};

const EMPTY: readonly ToastItem[] = Object.freeze([]);

let storeSequence = 0;

export function createToastStore(options: ToastStoreOptions = {}): ToastStore {
  const max = Math.max(1, options.max ?? TOAST_DEFAULT_MAX);
  const durations = { ...TOAST_DEFAULT_DURATIONS, ...options.defaultDurations };
  const storePrefix = `toast${++storeSequence}`;

  let counter = 0;
  let snapshot: readonly ToastItem[] = EMPTY;
  const listeners = new Set<ToastListener>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  const emit = () => {
    for (const listener of [...listeners]) listener();
  };

  const clearTimer = (id: string) => {
    const timer = timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(id);
    }
  };

  const dismiss = (id?: string) => {
    if (id === undefined) {
      if (snapshot.length === 0) return;
      for (const key of [...timers.keys()]) clearTimer(key);
      snapshot = EMPTY;
      emit();
      return;
    }
    clearTimer(id);
    if (!snapshot.some((t) => t.id === id)) return;
    snapshot = snapshot.filter((t) => t.id !== id);
    emit();
  };

  const add = (variant: ToastVariant, message: string, opts: ToastOptions = {}): string => {
    counter += 1;
    const id = `${storePrefix}-${counter}`;
    const durationMs = Math.max(0, opts.durationMs ?? durations[variant]);
    const item: ToastItem = {
      id,
      variant,
      message,
      description: opts.description,
      durationMs,
      dismissible: opts.dismissible ?? true,
      action: opts.action,
    };

    let next = [...snapshot, item];
    if (next.length > max) {
      const dropped = next.slice(0, next.length - max);
      for (const old of dropped) clearTimer(old.id);
      next = next.slice(next.length - max);
    }
    snapshot = next;

    if (durationMs > 0) {
      const timer = setTimeout(() => dismiss(id), durationMs);
      // Never keep a Node process alive for a toast timer.
      (timer as { unref?: () => void }).unref?.();
      timers.set(id, timer);
    }
    emit();
    return id;
  };

  const api: ToastApi = {
    success: (message, opts) => add("success", message, opts),
    error: (message, opts) => add("error", message, opts),
    info: (message, opts) => add("info", message, opts),
    warning: (message, opts) => add("warning", message, opts),
    dismiss,
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    add,
    dismiss,
    api,
    destroy: () => {
      for (const key of [...timers.keys()]) clearTimer(key);
      listeners.clear();
      snapshot = EMPTY;
    },
  };
}
